/*
 * AI-GENERATED
 * Microsoft 365 Copilot
 * LLM: GPT-5 reasoning
 * Tarkistanut Arttu Pietarinen
 * 2026-09-15
 */
import styled from 'styled-components';
import strings from '../../../translations';
import { useAppSelector } from '../../../state/hooks';
import { useContext, useEffect, useState } from 'react';
import { faMagnifyingGlass, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { emptySearchResults, validateFeatureSearch } from '../utils/SearchUtil';
import { ReactReduxContext } from 'react-redux';
import {
  setSearchValue,
  setFeatureErrors,
  setCurrentMetadata
} from '../../../state/slices/rpcSlice';
import { setAttributeSearchEnabled } from '../../../state/slices/uiSlice';
import Select from 'react-select';
import '../css/ReactSelectStyling.css';
import PillButton from '../../../utils/components/PillButton';
import useIndexSearch from '../utils/useIndexSearch';

const DEFAULT_LIMIT = 100;

// Layer metadata per search lambda, kept across mounts so switching search
// types doesn't refetch every metadata page. Keyed by requested service name.
const layerMetadataCache = new Map();

const StyledRowWithButton = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  flex-wrap: nowrap;
`;

const StyledInputsContainer = styled.div`
  display: flex;
  flex: 1 1 0;
  align-items: center;
  min-width: 0;

  @media ${(props) => props.theme.device.tablet} {
    width: 100%;
  }
`;

const StyledStandardSearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 0 0.5em;
  border: none;
  color: ${(props) => props.theme.colors.mainColor1};
  background: none;
  cursor: pointer;
  font-size: 1.2em;

  &:hover {
    opacity: 0.95;
  }
`;

const StyledRelativeInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PillInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-width: 64px;
  max-width: 130px;
  height: 42px;
  margin-bottom: 0;
  padding: 1em;
  border: 1.5px solid #ccc;
  border-radius: 20px;
  outline: none;
  background: #fff;
  text-align: center;
  transition: border-color 0.17s;

  @media ${(props) => props.theme.device.tablet} {
    min-width: 54px;
    max-width: 100%;
    height: 36px;
    font-size: 14px;
  }

  &.error {
    border-color: ${(props) =>
      props.theme.colors.secondaryColorDarkOrange || '#c55'};
  }

  &:focus {
    border-color: #888;
  }

  &:disabled {
    color: #aaa;
    background: #f7f8f8;
  }
`;

const StyledWideInputGroup = styled.div`
  width: 100%;
`;

const StyledWidePillInput = styled(PillInput)`
  width: 100%;
  min-width: 150px;
  max-width: 600px;
  padding-right: 44px;
  text-align: left;
`;

const StyledFeatureSearchSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 1em;
`;

const StyledValidationMessage = styled.div`
  margin-top: 8px;
  color: ${(props) => props.theme.colors.secondaryColorDarkOrange || '#c55'};
  font-size: 0.95em;
`;

const StyledSelectedLayerWrapper = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 4px;
  margin-left: 0.5em;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledSelectedLayerTitle = styled.div`
  color: ${(props) => props.theme.colors.mainColor1};
  font-size: 16px;
  font-weight: 500;
`;

const StyledSelectedLayerText = styled.div`
  overflow: hidden;
  margin-right: 0.5em;
  margin-left: 0.5em;
  font-size: 15px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledNoActiveLayers = styled.div`
  color: ${(props) => props.theme.colors.secondaryColorDarkOrange};
  font-size: 16px;
  font-weight: 500;
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 0.5em 0.5em;
`;

const StyledCheckbox = styled.input`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  margin-left: 0;
`;

const CheckboxLabel = styled.label`
  margin: 0;
  color: ${(props) => props.theme.colors.darkGrey || '#333'};
  font-size: 16px;
`;

const StyledAttributeSelectionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const StyledInstructionText = styled.p`
  margin-bottom: 0;
  margin-left: 0.5em;
`;

const StyledSearchButtons = styled.div`
  display: flex;
  width: 100%;
  gap: 1em;
  padding-top: 1em;

  @media ${(props) => props.theme.device.tablet} {
    flex-direction: column;
    gap: 8px;
  }
`;

const StyledInfoDiv = styled.div`
  color: ${(props) => props.theme.colors.secondaryColorDarkOrange || '#c55'};
`;

const parseFieldNameLocales = (data) => {
  if (!data) return [];

  return Object.entries(data)
    .map(([value, label]) => ({ value, label }))
    .sort((first, second) =>
      String(first.label ?? '').localeCompare(
        String(second.label ?? ''),
        undefined,
        {
          sensitivity: 'base'
        }
      )
    );
};

const splitLayerName = (layerName = '') => {
  const separatorIndex = layerName.indexOf(':');

  if (separatorIndex === -1) {
    return { workspace: '', layerName };
  }

  return {
    workspace: layerName.slice(0, separatorIndex),
    layerName: layerName.slice(separatorIndex + 1)
  };
};

const IndexSearchInput = ({ setDropdownOpen, emptySearchInputs }) => {
  const { store } = useContext(ReactReduxContext);
  const { executeIndexSearch } = useIndexSearch();
  const channel = useAppSelector((state) => state.rpc.channel);
  const selectedLayersByType = useAppSelector(
    (state) => state.rpc.selectedLayersByType
  );
  const featureErrors = useAppSelector((state) => state.rpc.featureErrors);
  const searchResults = useAppSelector((state) => state.rpc.searchResults);
  const searchValue = useAppSelector((state) => state.rpc.searchValue);
  const indexSearchResults = useAppSelector(
    (state) => state.rpc.indexSearchResults
  );
  const attributeSearchEnabled = useAppSelector(
    (state) => state.ui.attributeSearchEnabled
  );

  const [layerAttributes, setLayerAttributes] = useState([]);
  const [searchAttribute, setSearchAttribute] = useState('');
  const [partialMatch, setPartialMatch] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null);
  const [layerMetadata, setLayerMetadata] = useState([]);
  const [pendingMetadataServices, setPendingMetadataServices] = useState([]);

  const selectedLayer = selectedLayersByType.mapLayers[0] ?? null;
  const isSearching = indexSearchResults?.status === 'loading';
  const hasIndexResults = (indexSearchResults?.items?.length ?? 0) > 0;
  const hasResults = searchResults !== null || hasIndexResults;
  const [forcedPartialDisable, setForcedPartialDisable] = useState(false);
  const selectedLayerId = selectedLayer?.id ?? null;
  const [availableSearchLambdas, setAvailableSearchLambdas] = useState([]);

  useEffect(() => {
    channel.getAvailableSearchServices(
      [],
      (data) => {
        console.info('getAvailableSearchServices response', data);
        setAvailableSearchLambdas(data);
      },
      (error) => {
        console.error('getAvailableSearchServices request failed', error);
      }
    );
  }, [channel]);

  useEffect(() => {
    if (!channel || !selectedLayerId) {
      return undefined;
    }

    let cancelled = false;
    setSelectedOption(null);
    setSearchAttribute('');
    setLayerAttributes([]);

    channel.getFieldNameLocales(
      [selectedLayerId],
      (data) => {
        if (!cancelled) setLayerAttributes(parseFieldNameLocales(data));
      },
      (error) => {
        if (!cancelled)
          console.error('Field name locale request failed', error);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [channel, selectedLayerId]);

  useEffect(() => {
    if (!channel) return undefined;

    if (!availableSearchLambdas) return undefined;
    let cancelled = false;

    const servicesToFetch = availableSearchLambdas.filter(
      (service) => !layerMetadataCache.has(service)
    );
    setLayerMetadata(
      availableSearchLambdas
        .filter((service) => layerMetadataCache.has(service))
        .map((service) => layerMetadataCache.get(service))
    );
    setPendingMetadataServices(servicesToFetch);

    const finishService = (service) =>
      setPendingMetadataServices((pendingServices) =>
        pendingServices.filter((pending) => pending !== service)
      );

    const fetchMetadataPage = (
      offset,
      accumulatedResults,
      serviceName = 'avoindata'
    ) => {
      channel.searchIndex(
        [{ target: 'metadata', limit: DEFAULT_LIMIT, offset, serviceName }],
        (response, error) => {
          if (cancelled) return;

          if (error) {
            console.error('Search index metadata request failed', error);
            finishService(serviceName);
            return;
          }

          if (response?.target !== 'metadata') {
            console.error(
              `Unexpected search response target: ${response?.target}`
            );
            finishService(serviceName);
            return;
          }

          const results = [...accumulatedResults, ...(response.results ?? [])];
          const responseServiceName = response.serviceName ?? serviceName;
          const paging = response.paging ?? {
            limit: DEFAULT_LIMIT,
            offset,
            returned: 0
          };

          if (paging.hasMoreResults) {
            fetchMetadataPage(
              paging.offset + paging.returned,
              results,
              responseServiceName
            );
            return;
          }

          // State format: [{ service: 'avoindata', results: [...] }]
          const serviceMetadata = { service: responseServiceName, results };
          layerMetadataCache.set(serviceName, serviceMetadata);
          setLayerMetadata((currentLayerMetadata) => [
            ...currentLayerMetadata.filter(
              ({ service }) => service !== responseServiceName
            ),
            serviceMetadata
          ]);
          finishService(serviceName);
        }
      );
    };

    servicesToFetch.forEach((service) => {
      fetchMetadataPage(0, [], service);
    });
    return () => {
      cancelled = true;
    };
  }, [channel, availableSearchLambdas]);

  const getSearchMetadataForLayer = (layer) => {
    if (!layer) return null;

    const { workspace, layerName } = splitLayerName(layer.layerName);
    const chosenLayer =
      layerMetadata
        .flatMap(({ service, results }) =>
          results.map((metadata) => ({ service, metadata }))
        )
        .find(
          ({ metadata }) =>
            // metadata.maplayerId === layer.id ||
            metadata.gsWorkspace === workspace && metadata.gsName === layerName
        ) ?? null;

    console.info('chosenLayer', chosenLayer);
    return chosenLayer;
  };

  const onClickSearch = () => {
    const isValid = validateFeatureSearch(
      searchValue.trim(),
      store,
      true,
      attributeSearchEnabled
    );
    if (!isValid) {
      return;
    }
    if (attributeSearchEnabled && !searchAttribute) {
      store.dispatch(setFeatureErrors(['attributeNotSelected']));
      return;
    }
    const metadata = selectedLayer
      ? getSearchMetadataForLayer(selectedLayer)
      : null;

    if (!metadata && pendingMetadataServices.length > 0) {
      store.dispatch(setFeatureErrors(['metadataLoading']));
      return;
    }

    store.dispatch(setCurrentMetadata(metadata));
    if (!metadata) {
      store.dispatch(setFeatureErrors(['metadataNotFound']));
      emptySearchInputs();
      return;
    }
    store.dispatch(setFeatureErrors([]));
    setDropdownOpen(false);
    const searchParams = {
      query: searchValue.trim(),
      searchAttribute: attributeSearchEnabled ? searchAttribute : '',
      partialMatch,
      layerId: selectedLayer?.id ?? null,
      metadataId: metadata?.metadata?.id ?? null,
      offset: 0,
      serviceName: metadata?.service ?? null
    };
    console.info('searchParams', searchParams);
    executeIndexSearch(searchParams);
  };

  const clearResults = () => {
    emptySearchResults(store);
  };

  return (
    <StyledFeatureSearchSection id="feature-search-section">
      <StyledSelectedLayerWrapper>
        {selectedLayer ? (
          <>
            <StyledSelectedLayerTitle id="feature-search-layer-title">
              {strings.search.searchIndex.searchFromLayer}
            </StyledSelectedLayerTitle>
            <StyledSelectedLayerText id="feature-search-layer-text">
              {selectedLayer.name}
            </StyledSelectedLayerText>
          </>
        ) : (
          <StyledNoActiveLayers id="feature-search-no-active-layers" />
        )}
      </StyledSelectedLayerWrapper>

      <StyledCheckboxWrapper>
        <StyledCheckbox
          id="feature-search-partial-match-checkbox"
          aria-label="Käytä osittaista sanahakua"
          name="feature-search-partial-match-checkbox"
          type="checkbox"
          onChange={(event) => {
            const isEnabled = event.target.checked;
            setPartialMatch(isEnabled);
          }}
          checked={partialMatch}
          disabled={attributeSearchEnabled}
          aria-checked={partialMatch}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.click();
            }
          }}
        />
        <CheckboxLabel htmlFor="feature-search-partial-match-checkbox">
          {strings.search.searchIndex.partialMatch}
        </CheckboxLabel>
      </StyledCheckboxWrapper>

      {forcedPartialDisable && (
        <StyledCheckboxWrapper>
          <StyledInfoDiv>
            {strings.search.searchIndex.forcedPartialDisable}
          </StyledInfoDiv>
        </StyledCheckboxWrapper>
      )}
      <StyledCheckboxWrapper>
        <StyledCheckbox
          id="feature-search-attribute-checkbox"
          aria-label={strings.search.searchIndex.attributeSearch}
          name="feature-search-attribute-checkbox"
          type="checkbox"
          onChange={(event) => {
            const isEnabled = event.target.checked;

            if (isEnabled) {
              if (!partialMatch) {
                setPartialMatch(true);
                setForcedPartialDisable(true);
              } else {
                setForcedPartialDisable(false);
              }
            } else {
              setSelectedOption(null);
              setSearchAttribute('');
              setForcedPartialDisable(false);
            }

            store.dispatch(setAttributeSearchEnabled(isEnabled));
          }}
          checked={attributeSearchEnabled}
          aria-checked={attributeSearchEnabled}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.click();
            }
          }}
        />
        <CheckboxLabel htmlFor="feature-search-attribute-checkbox">
          {strings.search.searchIndex.attributeSearch}
        </CheckboxLabel>
      </StyledCheckboxWrapper>

      {attributeSearchEnabled && (
        <StyledAttributeSelectionSection id="attribute-selection-section">
          <StyledInstructionText id="attribute-selection-title">
            {strings.search.searchIndex.selectSearchAttribute}
          </StyledInstructionText>
          <div style={{ width: '100%', marginBottom: '0.5em' }}>
            <Select
              inputId="attribute-select"
              aria-label={strings.search.searchIndex.selectSearchAttribute}
              value={selectedOption}
              onChange={(option) => {
                setSelectedOption(option ?? null);
                setSearchAttribute(option?.value ?? '');
              }}
              options={layerAttributes}
              isSearchable
              placeholder="Select attribute..."
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : null
              }
              menuPosition="fixed"
              classNamePrefix="feature-search"
            />
          </div>
        </StyledAttributeSelectionSection>
      )}

      <StyledRowWithButton>
        <StyledInputsContainer>
          <StyledWideInputGroup>
            <StyledRelativeInputWrapper>
              <StyledWidePillInput
                id="feature-search-input"
                aria-label={
                  strings.search.searchIndex?.title || 'Fulltext search'
                }
                aria-describedby={
                  featureErrors.length > 0
                    ? 'feature-search-error-list'
                    : undefined
                }
                type="text"
                value={searchValue}
                disabled={isSearching}
                onChange={(event) =>
                  store.dispatch(setSearchValue(event.target.value))
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onClickSearch();
                }}
                className={featureErrors.length > 0 ? 'error' : ''}
              />
            </StyledRelativeInputWrapper>
          </StyledWideInputGroup>
        </StyledInputsContainer>

        {!isSearching && searchValue && (
          <StyledStandardSearchButton
            id="feature-search-clear-fields-button"
            type="button"
            aria-label={strings.search?.clearFields}
            onClick={emptySearchInputs}
          >
            <FontAwesomeIcon icon={faTrash} />
          </StyledStandardSearchButton>
        )}
      </StyledRowWithButton>

      {featureErrors.length > 0 && (
        <div id="feature-search-error-list">
          {featureErrors.map((error, index) => (
            <StyledValidationMessage
              id={`feature-search-error-${index}`}
              key={`feature-search-error-${index}-${error}`}
              role="alert"
              aria-live="polite"
            >
              {strings.search?.searchIndex?.errors?.[error] ?? error}
            </StyledValidationMessage>
          ))}
        </div>
      )}

      <StyledSearchButtons>
        <PillButton
          id="feature-search-submit-button"
          key="feature-search-submit-button"
          text={strings.search?.search}
          onClick={onClickSearch}
          aria-label={strings.search.search}
          style={{ width: '100%', justifyContent: 'center' }}
          icon={faMagnifyingGlass}
          disabled={isSearching}
        />
        <PillButton
          id="feature-search-inputs-clear-results-btn"
          key="feature-search-inputs-clear-results-btn"
          variant="inverse"
          text={strings.search?.clearResults}
          onClick={clearResults}
          aria-label={strings.search?.clearResults}
          style={{ width: '100%', justifyContent: 'center' }}
          disabled={!hasResults}
        />
      </StyledSearchButtons>
    </StyledFeatureSearchSection>
  );
};

export default IndexSearchInput;
